import * as ko from "knockout";
import {IMessageData, IObjectData, IResponse} from '../../../index';
import {global, lm} from "../../../appContext";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId, SPINNER, TEXT_WRAPPER_END, TEXT_WRAPPER_START} from "../../../constants";
import {createErrorMessage, getObjectDefinition} from "../../../util/misc";
import {nsFa} from "../../fa/fn";
import {getSearchObjectParams} from "../../../util/request";
import {REQUEST_MESSAGE} from "../../../constants/Language";
import i = nsFa.i;
import contextHtml from './context.html?raw';

export let template = contextHtml;

interface IParams {
    objectid: KnockoutObservable<number | null>
    objectTypeId: KnockoutObservable<ObjectTypeId | null>
}

export class viewModel {
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly objectid: KnockoutObservable<number | null>;
    private readonly objectTypeId: KnockoutObservable<ObjectTypeId | null>;
    private readonly reqSearchObjectIds = global.getPromiseRequest('searchobject');
    private readonly reqEvaluateexpression = global.getPromiseRequest('evaluateexpression');
    private readonly objectDisplayName: KnockoutObservable<string | null> = ko.observable(null);
    private readonly objectData: KnockoutObservable<IObjectData | null> = ko.observable(null);
    private readonly allObjectIds: KnockoutObservableArray<number> = ko.observableArray<number>([]);

    constructor(params: IParams) {
        this.objectid = params.objectid;
        this.objectTypeId = params.objectTypeId;

        this.kosub.subscribe(this.objectid, () => {
            this.fetchObjectDisplayName();
        });
        this.kosub.subscribe(this.objectData, () => {
            const data = this.objectData();
            this.objectid(data ? data.id : null);
        });
        this.kosub.subscribeChanged(this.objectTypeId, () => {
            this.objectid(null)
        })
        if (this.objectid()) {
            this.fetchObjectDisplayName();
        }
    }

    private objectDefinition = ko.pureComputed(() => {
        const typeId = this.objectTypeId();
        return typeId != null ? getObjectDefinition(typeId) : null;
    })

    private onClickRandomObject() {
        Promise
            .resolve()
            .then(() => {
                if (!this.allObjectIds().length) {
                    return this.fetchIds();
                }
            })
            .then(() => {
                this.objectid(null);
                const ids = this.allObjectIds();
                const randomIndex = Math.floor(Math.random() * ids.length);
                const id = ids[randomIndex];
                this.objectid(id ?? null);
            })
            .catch(reason => this.messageData(createErrorMessage(String(reason))));
    }

    private fetchIds(): Promise<void> {
        this.messageData({message: REQUEST_MESSAGE});
        return this.reqSearchObjectIds
            .request(getSearchObjectParams({
                objecttypeid: this.objectTypeId()!,
                onlyids: true,
                resultsprpage: MAX_RESULTS_PER_PAGE,
            }))
            .then((data: number[]) => {
                this.allObjectIds(data);
            })
            .then(() => {
                const hasIds = !!this.allObjectIds().length;
                if (hasIds) {
                    this.messageData(null);
                } else {
                    this.messageData({message: `none available`});
                }
            });
    }

    private fetchObjectDisplayName() {
        if (!this.objectid()) {
            this.objectDisplayName('—');
            return;
        }

        this.objectDisplayName(SPINNER);

        const objDef = this.objectDefinition();
        if (!objDef) {
            return;
        }

        const displayNames = objDef.syncfieldDisplayName ?? [];
        const expression = displayNames.reduce((ret: string, str: string): string => {
            return ret.concat(`[IFEMPTYUSE;[${str.toLowerCase()}];`);
        }, '').concat(new Array(displayNames.length).fill(']').join(''));

        global.getPromiseRequest('evaluateexpression')
            .request({
                expression: expression,
                objectid: this.objectid()!,
            })
            .then((data: string[]) => {
                this.objectDisplayName(data[0] ?? null);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
                this.objectDisplayName(null)
            });
    }

    private onClickSelectObject() {
        const typeId = this.objectTypeId();
        if (!typeId) {
            return;
        }
        const objDef = getObjectDefinition(typeId);
        if (!objDef) {
            return;
        }
        global.dialogManager.addComponent({
            title: `${i(objDef.icon)} ${lm.t(`choose subject|s:((${objDef.name}|ac))`) ?? ''}`,
            content: {
                name: 'component/object/dialog/pick',
                params: {
                    selectedObject: this.objectData,
                    objecttypeid: typeId,
                }
            }
        });
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.reqSearchObjectIds.abort();
        this.reqEvaluateexpression.abort();
    }
}
